import { useCallback, useEffect, useRef } from 'react';
import { loadView, saveView, clearView, VIEW_KEYS, TERRAIN_KEYS, LAYER_KEYS } from '../lib/view-state';
import { lonLatToLocal } from '../lib/view-sync';
import { insideExpanded } from '../lib/camera-framing';
import { getTerrainSettings, setTerrainSettings } from '../lib/terrain-settings';

const ADOPT = Symbol('adopt'); // the next settled state becomes the baseline without being written
const TERRAIN_DEFAULTS = { drape3d: true, drapeStyle: 'hillshade', ve3d: 1, surfaceOpacity3d: 0.6 };

/**
 * The saved camera in the current scene's frame: shifted by the offset between the saved and the current origin (the local
 * frame follows the loaded models), or null when its target falls outside the drawn content (the saved models are not
 * loaded). Entries without an origin are taken as the same frame.
 */
function placeCamera(saved, viewer) {
  const cur = viewer.getSceneOrigin?.() || null;
  let { position, target } = saved;
  if (saved.origin) {
    if (!cur) return null;
    const [dx, dz] = lonLatToLocal([saved.origin.lon, saved.origin.lat], { lon0: cur.lon, lat0: cur.lat });
    position = [position[0] + dx, position[1], position[2] + dz];
    target = [target[0] + dx, target[1], target[2] + dz];
  }
  if (!insideExpanded(viewer.getContentBox?.() || null, target)) return null;
  return { position, target };
}

const pickKeys = (src, keys) => Object.fromEntries(keys.map((k) => [k, src[k]]));

/**
 * Saved 3D view of a project (lib/view-state.js): restores it once the page's models are loaded (`ready`) and saves it
 * 1 s after the last change. The camera and the scene-local state (colour table, iso-surfaces, slices, cutting plane) are
 * read through `viewerRef` (SubsurfaceViewer3D's imperative handle), so changes there are noticed by comparing a snapshot
 * every second: a snapshot is written when it has stopped changing between two ticks and differs from the saved one.
 * `layers` is { visible, restore, defaults }: the /3d page's base-layer toggles (Terrain, Point Cloud, Projects), referentially stable.
 * `view` is the useViewer3dView state object; `onResetCamera` puts the camera back to its fitted position.
 */
export default function useViewer3dPersistence({ projectId, view, viewerRef, ready, onResetCamera, storage, intervalMs = 1000, datasetTicks = null, layers = null }) {
  const latest = useRef({});
  latest.current = { view, onResetCamera, datasetTicks, layers };
  const last = useRef(null);
  const saved = useRef(ADOPT);
  const pendingCamera = useRef(null);
  const restoredFor = useRef(null);
  const keptCamera = useRef(null); // saved camera that was not applied (other models): kept until the user moves the camera
  const baseCamera = useRef(null); // the camera the scene opened with, as text

  const snapshot = useCallback(() => {
    const v = latest.current.view;
    const viewer = viewerRef.current;
    const pose = viewer?.getCameraPose?.() || undefined;
    const origin = viewer?.getSceneOrigin?.() || undefined;
    return {
      view: pickKeys(v, VIEW_KEYS),
      camera: pose && origin ? { ...pose, origin } : pose,
      extras: viewer?.getViewExtras?.() || undefined,
      terrain: pickKeys(getTerrainSettings(), TERRAIN_KEYS),
      layers: latest.current.layers ? pickKeys(latest.current.layers.visible, LAYER_KEYS) : undefined,
      datasets: latest.current.datasetTicks ? { drawn: latest.current.datasetTicks.drawnIds, scatter: latest.current.datasetTicks.scatterIds } : undefined,
    };
  }, [viewerRef]);

  // Restore once per project, when its models are in
  useEffect(() => {
    if (!projectId || !ready || restoredFor.current === projectId) return;
    restoredFor.current = projectId;
    saved.current = ADOPT;
    last.current = null;
    keptCamera.current = null;
    baseCamera.current = null;
    const s = loadView(projectId, storage);
    if (!s) return;
    if (s.view) latest.current.view.applyState(s.view);
    if (s.terrain) setTerrainSettings(s.terrain);
    if (s.extras) viewerRef.current?.setViewExtras?.(s.extras);
    if (s.layers) latest.current.layers?.restore?.(s.layers);
    if (s.datasets) latest.current.datasetTicks?.restore?.(s.datasets);
    pendingCamera.current = s.camera || null;
  }, [projectId, ready, storage, viewerRef]);

  // Autosave: 1 s after the state settles
  useEffect(() => {
    if (!projectId || !ready) return undefined;
    const id = setInterval(() => {
      if (restoredFor.current !== projectId) return;
      const viewer = viewerRef.current;
      if (pendingCamera.current && viewer?.getCameraPose?.()) {
        const placed = placeCamera(pendingCamera.current, viewer);
        if (placed) viewer.setCameraPose(placed);
        else keptCamera.current = pendingCamera.current;
        pendingCamera.current = null;
      }
      let snap = snapshot();
      const text = JSON.stringify(snap);
      if (text === last.current && text !== saved.current) {
        if (saved.current === ADOPT) baseCamera.current = JSON.stringify(snap.camera || null);
        else {
          // the saved camera of other models stays until the camera is moved
          if (keptCamera.current && JSON.stringify(snap.camera || null) === baseCamera.current) snap = { ...snap, camera: keptCamera.current };
          else keptCamera.current = null;
          saveView(projectId, snap, storage);
        }
        saved.current = text;
      }
      last.current = text;
    }, intervalMs);
    return () => clearInterval(id);
  }, [projectId, ready, storage, intervalMs, snapshot, viewerRef]);

  /** Forget the saved view and return the scene to its defaults (the camera goes back to its fitted position). */
  const resetSavedView = useCallback(() => {
    clearView(projectId, storage);
    pendingCamera.current = null;
    keptCamera.current = null;
    saved.current = ADOPT;
    last.current = null;
    latest.current.view.resetViewState();
    setTerrainSettings(TERRAIN_DEFAULTS);
    viewerRef.current?.setViewExtras?.(null);
    latest.current.datasetTicks?.restore?.({ drawn: [], scatter: [] });
    latest.current.layers?.restore?.(latest.current.layers.defaults);
    latest.current.onResetCamera?.();
  }, [projectId, storage, viewerRef]);

  return { resetSavedView };
}
